import React from "react";
import { motion } from "framer-motion";

function Module4() {
  return (
    <>
      <div className="bg-particles" />
      <div className="page-container">
        <motion.div
          className="module-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="module-number">Módulo 04</p>
          <h1 className="module-title">MCP Gateway</h1>
          <p className="module-description">
            AgentCore Gateway permite desacoplar agentes de herramientas con un patrón
            centralizado de gobernanza. Control fino sobre quién usa qué herramienta.
          </p>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2>🔗 ¿Qué es MCP Gateway?</h2>
          <p>
            Model Context Protocol (MCP) es un estándar abierto para conectar agentes de IA
            con herramientas externas. AgentCore Gateway implementa este patrón como servicio
            gestionado, proporcionando:
          </p>
          <ul>
            <li><strong>Descubrimiento Semántico:</strong> Los agentes encuentran herramientas por descripción, no por endpoint</li>
            <li><strong>Conectividad Privada:</strong> Las herramientas se exponen via VPC sin acceso público</li>
            <li><strong>Control de Acceso:</strong> Políticas granulares sobre qué agente puede usar qué tool</li>
            <li><strong>Gobernanza Central:</strong> Un solo punto para auditar todas las invocaciones de tools</li>
            <li><strong>Versionamiento:</strong> Múltiples versiones de tools sin romper agentes existentes</li>
          </ul>
          <div className="architecture-diagram">
            <div className="flow">
              <div className="node">Agente Nequito</div>
              <div className="arrow">→</div>
              <div className="node">AgentCore Gateway</div>
              <div className="arrow">→</div>
              <div className="node">API Promociones</div>
            </div>
            <div className="flow" style={{ marginTop: "16px" }}>
              <div className="node" style={{ visibility: "hidden" }}>Spacer</div>
              <div className="arrow" style={{ visibility: "hidden" }}>→</div>
              <div className="node" style={{ border: "1px solid #FF9900" }}>Gateway MCP</div>
              <div className="arrow">→</div>
              <div className="node">Lambda Tienda</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2>⚙️ Paso 0: Crear APIs y Lambdas</h2>
          <p>
            Primero creamos la infraestructura que el Gateway va a exponer como herramientas MCP:
          </p>
          <h3>API Gateway - Promociones Nequi</h3>
          <div className="code-block">
{`# Endpoints de la API de Promociones:
# GET  /promociones         → Lista promociones activas
# POST /promociones/redimir → Redime una promocion/descuento

# Ejemplo de respuesta GET /promociones:
{
  "promociones": [
    {
      "id": "PROMO-001",
      "nombre": "20% SOAT Digital Nequi",
      "descripcion": "Descuento en SOAT digital",
      "puntos_requeridos": 500,
      "vigencia": "2025-12-31"
    },
    {
      "id": "PROMO-002",
      "nombre": "Curso Gratis de IA",
      "descripcion": "Acceso a curso de Inteligencia Artificial",
      "puntos_requeridos": 200,
      "vigencia": "2025-09-30"
    }
  ]
}`}
          </div>

          <h3>Lambda - Tienda Nequi Online</h3>
          <div className="code-block">
{`# Lambda: nequi_tienda_online
# Acciones: GET items, Comprar con puntos

# GET items - Retorna items aleatorios de la tienda
{
  "items": [
    {"id": "ITEM-101", "nombre": "Camiseta Nequi", "puntos": 300},
    {"id": "ITEM-102", "nombre": "Termo Eco", "puntos": 450},
    {"id": "ITEM-103", "nombre": "Mochila Aventura", "puntos": 800}
  ],
  "puntos_disponibles": 1500  // Asignados aleatoriamente
}

# POST comprar - Compra un item con puntos
# Body: {"item_id": "ITEM-101"}
# Response: {"exito": true, "puntos_restantes": 1200}`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2>🛠️ Crear el AgentCore Gateway</h2>
          <p>
            Una vez creadas las APIs y Lambdas, creamos el Gateway MCP manualmente
            y registramos las herramientas:
          </p>
          <div className="step-card">
            <h4>Paso 1: Crear el Gateway</h4>
            <p>Desde la consola de AgentCore, crear un nuevo Gateway con nombre "nequito-tools"</p>
          </div>
          <div className="step-card">
            <h4>Paso 2: Registrar Tool "promociones"</h4>
            <p>Agregar la API de promociones como herramienta MCP con descripción semántica</p>
          </div>
          <div className="step-card">
            <h4>Paso 3: Registrar Tool "tienda_online"</h4>
            <p>Agregar la Lambda de tienda como herramienta MCP</p>
          </div>
          <div className="step-card">
            <h4>Paso 4: Configurar Políticas</h4>
            <p>Definir que solo el agente "Nequito" puede invocar estas herramientas</p>
          </div>
          <div className="step-card">
            <h4>Paso 5: Integrar con el Agente</h4>
            <p>El agente LangGraph descubre y usa las herramientas via el Gateway URL</p>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2>🎯 Beneficios del Patrón MCP Gateway</h2>
          <div className="highlight-box">
            <h4>Desacoplamiento Total</h4>
            <p>
              Los agentes no necesitan saber los endpoints, credenciales o configuración
              de las herramientas. Solo necesitan el URL del Gateway y pueden descubrir
              las tools disponibles por búsqueda semántica.
            </p>
          </div>
          <ul>
            <li><strong>Seguridad:</strong> Conectividad privada via VPC, sin exposición pública</li>
            <li><strong>Auditoría:</strong> Cada invocación queda registrada con metadata del agente</li>
            <li><strong>Escalabilidad:</strong> Agregar nuevas tools sin modificar el código del agente</li>
            <li><strong>Multi-tenant:</strong> Diferentes agentes con diferentes permisos sobre las mismas tools</li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="source-links-banner">
            <h4>📖 Documentación Oficial AWS</h4>
            <div className="source-links-list">
              <div className="source-link-item">
                <span>MCP Protocol →</span>
                <a href="https://modelcontextprotocol.io/introduction" target="_blank" rel="noopener noreferrer">
                  modelcontextprotocol.io/introduction
                </a>
              </div>
              <div className="source-link-item">
                <span>API Gateway →</span>
                <a href="https://docs.aws.amazon.com/apigateway/latest/developerguide/welcome.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/apigateway/latest/developerguide/welcome.html
                </a>
              </div>
              <div className="source-link-item">
                <span>Lambda →</span>
                <a href="https://docs.aws.amazon.com/lambda/latest/dg/welcome.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/lambda/latest/dg/welcome.html
                </a>
              </div>
              <div className="source-link-item">
                <span>AgentCore Gateway →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/agentcore-gateway.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/agentcore-gateway.html
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}

export default Module4;
